import { useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import globalToast from "../../components/common/globalToast";
import AuthSplitLayout, {
    authEyeSx,
    authFormSx,
    authLinkSx,
    authSubmitSx,
    gold,
    line,
} from "../../components/AuthSplitLayout";
import {
    sendResetPasswordTokenPublic,
    updatePasswordPublic,
    validateResetPasswordTokenPublic,
} from "../../helpers/http_password_requests";

const copyByStep = [
    {
        title: "Redefinir senha",
        lead: "Informe o e-mail da conta para receber o código.",
    },
    {
        title: "Confirmar código",
        lead: "Digite o código enviado ao seu e-mail.",
    },
    {
        title: "Nova senha",
        lead: "Escolha uma senha com pelo menos 8 caracteres.",
    },
    {
        title: "Senha redefinida",
        lead: "Você já pode entrar com a nova senha.",
    },
];

export default function ResetPasswordPublic() {
    const [step, setStep] = useState(0);
    const [email, setEmail] = useState("");
    const [token, setToken] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSendToken = async (event) => {
        event.preventDefault();
        setLoading(true);
        const response = await sendResetPasswordTokenPublic(email);
        setLoading(false);
        if (response.success) {
            globalToast.success("Código enviado para seu e-mail.");
            setStep(1);
        } else {
            globalToast.error(response.message || "Não foi possível enviar o código.");
        }
    };

    const handleValidateToken = async (event) => {
        event.preventDefault();
        setLoading(true);
        const response = await validateResetPasswordTokenPublic(token, email);
        setLoading(false);
        if (response.success) {
            setStep(2);
        } else {
            globalToast.error(response.message || "Código inválido.");
        }
    };

    const handleUpdatePassword = async (event) => {
        event.preventDefault();
        if (!password || !confirmPassword) {
            globalToast.error("Preencha todos os campos.");
            return;
        }
        if (password !== confirmPassword) {
            globalToast.error("As senhas não coincidem.");
            return;
        }
        if (password.length < 8) {
            globalToast.error("A senha deve ter pelo menos 8 caracteres.");
            return;
        }
        setLoading(true);
        const response = await updatePasswordPublic(token, password);
        setLoading(false);
        if (response.success) {
            setStep(3);
        } else {
            globalToast.error(response.message || "Não foi possível redefinir a senha.");
        }
    };

    const { title, lead } = copyByStep[step];

    return (
        <AuthSplitLayout title={title} lead={lead}>
            {step < 3 ? (
                <Box sx={styles.steps} aria-hidden>
                    {[0, 1, 2].map((index) => (
                        <Box
                            key={index}
                            sx={{
                                ...styles.stepBar,
                                bgcolor: index <= step ? gold : line,
                            }}
                        />
                    ))}
                </Box>
            ) : null}

            {step === 0 && (
                <Box component="form" sx={authFormSx} onSubmit={handleSendToken} noValidate>
                    <TextField
                        label="E-mail"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        fullWidth
                        required
                    />
                    <Button
                        type="submit"
                        color="secondary"
                        variant="contained"
                        fullWidth
                        disabled={loading}
                        sx={authSubmitSx}
                    >
                        {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Enviar código"}
                    </Button>
                    <Typography component={Link} to="/login" sx={authLinkSx}>
                        Voltar ao login
                    </Typography>
                </Box>
            )}

            {step === 1 && (
                <Box component="form" sx={authFormSx} onSubmit={handleValidateToken} noValidate>
                    <TextField
                        label="Código"
                        value={token}
                        onChange={(event) => setToken(event.target.value)}
                        fullWidth
                        required
                    />
                    <Button
                        type="submit"
                        color="secondary"
                        variant="contained"
                        fullWidth
                        disabled={loading}
                        sx={authSubmitSx}
                    >
                        {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Validar código"}
                    </Button>
                    <Typography component={Link} to="/login" sx={authLinkSx}>
                        Voltar ao login
                    </Typography>
                </Box>
            )}

            {step === 2 && (
                <Box component="form" sx={authFormSx} onSubmit={handleUpdatePassword} noValidate>
                    <TextField
                        label="Nova senha"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        fullWidth
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                                        onClick={() => setShowPassword((open) => !open)}
                                        edge="end"
                                        sx={authEyeSx}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label="Repita a nova senha"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        fullWidth
                        required
                    />
                    <Button
                        type="submit"
                        color="secondary"
                        variant="contained"
                        fullWidth
                        disabled={loading}
                        sx={authSubmitSx}
                    >
                        {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Redefinir senha"}
                    </Button>
                    <Typography component={Link} to="/login" sx={authLinkSx}>
                        Voltar ao login
                    </Typography>
                </Box>
            )}

            {step === 3 && (
                <Box sx={authFormSx}>
                    <Button
                        component={Link}
                        to="/login"
                        color="secondary"
                        variant="contained"
                        fullWidth
                        sx={authSubmitSx}
                    >
                        Ir para o login
                    </Button>
                </Box>
            )}
        </AuthSplitLayout>
    );
}

const styles = {
    steps: {
        display: "flex",
        gap: 1,
        mb: 3,
    },
    stepBar: {
        flex: 1,
        height: 3,
        borderRadius: 1,
    },
};
